import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { SermonSectionComponent } from '../components/SermonSectionComponent';

interface SermonSectionOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    sermonSection: {
      setSermonSection: (type: string, pointNumber?: string, attribution?: string, isSlide?: boolean) => ReturnType;
      moveSermonSection: (pos: number, targetPos: number) => ReturnType;
    };
  }
}

export const SermonSection = Node.create<SermonSectionOptions>({
  name: 'sermonSection',
  
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      type: {
        default: 'scripture',
        parseHTML: element => element.getAttribute('data-type'),
        renderHTML: attributes => {
          return {
            'data-type': attributes.type,
            'class': `sermon-section sermon-section-${attributes.type}`,
          };
        },
      },
      pointNumber: {
        default: null,
        parseHTML: element => element.getAttribute('data-point-number'),
        renderHTML: attributes => attributes.pointNumber ? { 'data-point-number': attributes.pointNumber } : {},
      },
      attribution: {
        default: null,
        parseHTML: element => element.getAttribute('data-attribution'),
        renderHTML: attributes => attributes.attribution ? { 'data-attribution': attributes.attribution } : {},
      },
      isSlide: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-slide') === 'true',
        renderHTML: attributes => attributes.isSlide ? { 'data-is-slide': 'true' } : {},
      },
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-section-id'),
        renderHTML: attributes => attributes.id ? { 'data-section-id': attributes.id } : {},
      },
      customTitle: {
        default: null,
        parseHTML: element => element.getAttribute('data-custom-title'),
        renderHTML: attributes => attributes.customTitle ? { 'data-custom-title': attributes.customTitle } : {},
      }
    };
  },

  group: 'block',
  content: 'block+',
  isolating: true,
  draggable: true, // Make sermon sections draggable

  // Improve performance by limiting updates
  shouldUpdate(oldNode, newNode) {
    return (
      oldNode.attrs.type !== newNode.attrs.type ||
      oldNode.attrs.pointNumber !== newNode.attrs.pointNumber ||
      oldNode.attrs.attribution !== newNode.attrs.attribution ||
      oldNode.attrs.isSlide !== newNode.attrs.isSlide ||
      oldNode.attrs.id !== newNode.attrs.id ||
      oldNode.attrs.customTitle !== newNode.attrs.customTitle ||
      oldNode.content.size !== newNode.content.size
    );
  },

  parseHTML() {
    return [
      {
        tag: 'div.sermon-section',
        getAttrs: element => {
          if (typeof element === 'string') return {};
          const dom = element as HTMLElement;
          const type = dom.getAttribute('data-type') || 'scripture';
          const pointNumber = dom.getAttribute('data-point-number');
          const attribution = dom.getAttribute('data-attribution');
          const isSlide = dom.getAttribute('data-is-slide') === 'true';
          const id = dom.getAttribute('data-section-id');
          const customTitle = dom.getAttribute('data-custom-title');
          return { type, pointNumber, attribution, isSlide, id, customTitle };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Filter out any non-string values and invalid attribute names
    const validAttributes = Object.entries(HTMLAttributes).reduce((acc, [key, value]) => {
      if (typeof key === 'string' && /^[a-zA-Z][\w-]*$/.test(key) && value != null) {
        acc[key] = value.toString();
      }
      return acc;
    }, {} as Record<string, string>);

    // Merge with base attributes
    const attrs = mergeAttributes(this.options.HTMLAttributes, validAttributes);
    
    return ['div', attrs, 0];
  },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { $from } = editor.state.selection;
        
        // Check if we're in an empty paragraph within a sermon section
        const isEmptyParagraph = $from.parent.type.name === 'paragraph' && $from.parent.content.size === 0;
        
        // Check if we're inside a sermon section
        let isInSermonSection = false;
        let sectionDepth = 0;
        
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === this.name) {
            isInSermonSection = true;
            sectionDepth = depth;
            break;
          }
        }
        
        if (isEmptyParagraph && isInSermonSection) {
          // Create a new paragraph after the section
          return editor
            .chain()
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const pos = $from.before(sectionDepth) + $from.node(sectionDepth).nodeSize;
                tr.insert(pos, editor.schema.nodes.paragraph.create());
                tr.setSelection(tr.selection.constructor.near(tr.doc.resolve(pos + 1)));
              }
              return true;
            })
            .focus()
            .run();
        }
        
        return false;
      },
      Escape: ({ editor }) => {
        const { $from } = editor.state.selection;
        
        // Check if we're inside a sermon section
        let isInSermonSection = false;
        let sectionDepth = 0;
        
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === this.name) {
            isInSermonSection = true;
            sectionDepth = depth;
            break;
          }
        }
        
        if (isInSermonSection) {
          // Create a new paragraph after the section
          return editor
            .chain()
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const pos = $from.before(sectionDepth) + $from.node(sectionDepth).nodeSize;
                tr.insert(pos, editor.schema.nodes.paragraph.create());
                tr.setSelection(tr.selection.constructor.near(tr.doc.resolve(pos + 1)));
              }
              return true;
            })
            .focus()
            .run();
        }
        
        return false;
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(SermonSectionComponent, {
      // Add a key to force re-render when needed
      contentDOMAttributes: { class: 'sermon-section-content' },
    });
  },

  addCommands() {
    return {
      setSermonSection:
        (type: string, pointNumber?: string, attribution?: string, isSlide: boolean = false) =>
        ({ commands, editor }) => {
          // For main-point sections, auto-calculate point number if not provided
          let finalPointNumber = pointNumber;
          if (type === 'main-point' && !pointNumber) {
            // Count existing main-point sections before the current cursor position
            let pointCount = 0;
            const currentPos = editor.state.selection.from;
            editor.state.doc.descendants((node, pos) => {
              if (pos < currentPos && 
                  node.type.name === this.name && 
                  node.attrs.type === 'main-point') {
                pointCount++;
              }
              return true;
            });
            finalPointNumber = String(pointCount + 1);
          }
          
          return commands.wrapIn(this.name, { 
            type, 
            pointNumber: finalPointNumber, 
            attribution, 
            isSlide, 
            id: crypto.randomUUID() 
          });
        },
      moveSermonSection:
        (pos: number, targetPos: number) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            const $pos = tr.doc.resolve(pos);
            let sectionStart = pos;
            let sectionDepth = 0;
            
            // Find the sermon section node
            for (let depth = $pos.depth; depth > 0; depth--) {
              const node = $pos.node(depth);
              if (node.type.name === this.name) {
                sectionStart = $pos.before(depth);
                sectionDepth = depth;
                break;
              }
            }
            
            if (sectionDepth > 0) {
              const sectionNode = $pos.node(sectionDepth);
              const sectionEnd = sectionStart + sectionNode.nodeSize;
              
              // Delete the section from its current position
              const deletedNode = tr.doc.nodeAt(sectionStart);
              tr.delete(sectionStart, sectionEnd);
              
              // Insert it at the target position
              if (deletedNode) {
                tr.insert(targetPos < sectionStart ? targetPos : targetPos - sectionNode.nodeSize, deletedNode);
              }
            }
          }
          
          return true;
        },
    };
  },
});